/**
 * Content-Disposition 헤더에서 파일명을 추출하는 유틸리티 함수들
 */

/**
 * HTTP 응답 헤더에서 Content-Disposition 값을 추출합니다.
 * 대소문자 구분 없이 다양한 형태의 헤더명을 지원합니다.
 * @param {Object} headers - HTTP 응답 헤더 객체
 * @returns {string|null} Content-Disposition 헤더 값 또는 null
 */
export function getContentDisposition(headers) {
    if (!headers) return null;

    return headers["content-disposition"] || headers["Content-Disposition"] || headers["CONTENT-DISPOSITION"] || null;
}

/**
 * Content-Disposition 헤더에서 파일명을 추출합니다.
 * 1순위: filename* (RFC 5987 형식)
 * 2순위: filename (일반 형식)
 * @param {string} contentDisposition - Content-Disposition 헤더 값
 * @returns {string|null} 추출된 파일명 또는 null
 */
export function extractFilenameFromContentDisposition(contentDisposition) {
    if (!contentDisposition) return null;

    // 1순위: filename* (RFC 5987 형식) - UTF-8 인코딩된 파일명
    const filenameStarMatch = contentDisposition.match(/filename\*[^;=\n]*=([^;\n]*)/);
    if (filenameStarMatch && filenameStarMatch[1]) {
        try {
            // filename*=UTF-8''파일명 형식에서 파일명 부분만 추출
            const filenameStarValue = filenameStarMatch[1];
            if (filenameStarValue.startsWith("UTF-8''")) {
                const encodedFilename = filenameStarValue.substring(7); // "UTF-8''" 제거
                return decodeURIComponent(encodedFilename);
            }
            // 다른 인코딩 형식인 경우 그대로 반환
            return decodeURIComponent(filenameStarValue);
        } catch (error) {
            console.warn("filename* 디코딩 실패:", error);
        }
    }

    // 2순위: filename (일반 형식)
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
        try {
            // 따옴표 제거 및 디코딩
            const filenameValue = filenameMatch[1].replace(/['"]/g, "");
            return decodeURIComponent(filenameValue);
        } catch (error) {
            console.warn("filename 디코딩 실패:", error);
        }
    }

    return null;
}

/**
 * HTTP 응답에서 파일명을 추출하는 통합 함수
 * @param {Object} response - HTTP 응답 객체
 * @param {string} defaultFilename - 기본 파일명 (추출 실패 시 사용)
 * @returns {string} 추출된 파일명 또는 기본 파일명
 */
export function extractFilenameFromResponse(response, defaultFilename) {
    if (!response || !response.headers) {
        return defaultFilename;
    }

    const contentDisposition = getContentDisposition(response.headers);
    if (!contentDisposition) {
        console.log("Content-Disposition 헤더를 찾을 수 없습니다.");
        return defaultFilename;
    }

    const extractedFilename = extractFilenameFromContentDisposition(contentDisposition);
    return extractedFilename || defaultFilename;
}
